import dayjs, { type Dayjs } from 'dayjs'

import type { TaxBehavior } from '~/app/settings/(billing)/taxes/components/taxOptions'
import { BILLING_PERIODS, BILLING_TYPES, type ProductFormData } from '~/app/settings/products/types'
import {
  getSafeCurrency,
  parseProductFormData,
  parseTimeOfUseRatesToForm,
  toFormEntitySelections
} from '~/app/settings/products/utils'
import type {
  BillableTimeOfUseRate,
  CreateProductRequest,
  FlatRatePricing,
  PerUnitPricing,
  ProductEntitySelectionResponse,
  TariffPassThroughPricing,
  TimeOfUsePricing,
  VolumePricing
} from '~/data/api/client/billing'
import {
  PRODUCT_VISIBILITIES,
  type PricingModel,
  TIER_QUANTITY_SOURCES,
  type UsagePricingType,
  VOLUME_TIERS_MODES
} from '~/data/types/billing'
import { parseFromSmallestDenomination } from '~/data/utils/currency'

import type { DraftApplyResult, ProductDraftFields, SkippedDraftField } from '../types'
import { isFiniteNumber, isOneOf, isRecord, isStringArray, skipped } from './shared'

export type ProductDraftToFormOptions = {
  /** Editing an existing product: type, visibility, pricing model, usage pricing type and currency are frozen. */
  isEditing: boolean
  /** Builds "now" in the user's zone for time pickers; defaults to plain dayjs. */
  tzDayjs?: () => Dayjs
}

const PRODUCT_TYPES = [
  'CHARGER_SUBSCRIPTION',
  'FLEET_CHARGING',
  'HOME_CHARGING',
  'OTHER',
  'PUBLIC_CHARGING',
  'VEHICLE_SUBSCRIPTION',
  'WORKPLACE_CHARGING',
  'RESERVATION',
  'CHARGING_ACCESS',
  'FLEET_MANAGEMENT',
  'INFRASTRUCTURE',
  'NETWORK_SERVICES',
  'SUPPORT_SERVICES'
] as const satisfies readonly CreateProductRequest['type'][]

const TAX_BEHAVIORS = ['INCLUSIVE', 'EXCLUSIVE'] as const satisfies readonly TaxBehavior[]

type PricingKey = 'flatRatePricing' | 'perUnitPricing' | 'timeOfUsePricing' | 'volumePricing' | 'tariffPassThroughPricing'

const PRICING_TARGET: Record<PricingKey, { pricingModel: PricingModel; usagePricingType?: UsagePricingType }> = {
  flatRatePricing: { pricingModel: 'FLAT' },
  perUnitPricing: { pricingModel: 'USAGE_BASED', usagePricingType: 'PER_UNIT' },
  timeOfUsePricing: { pricingModel: 'USAGE_BASED', usagePricingType: 'TIME_OF_USE' },
  volumePricing: { pricingModel: 'VOLUME_BASED' },
  tariffPassThroughPricing: { pricingModel: 'USAGE_BASED', usagePricingType: 'TARIFF_PASS_THROUGH' }
}

/**
 * Maps a (possibly partial) product draft — the billing-api request shape, money in cents — onto the
 * wizard's form state. Never throws: unknown keys, unsupported values and immutable-on-edit fields are
 * reported in `skipped` so the UI can explain them, and everything else lands in `next`.
 */
export function productDraftToForm(
  fields: ProductDraftFields,
  current: ProductFormData,
  options: ProductDraftToFormOptions
): DraftApplyResult<ProductFormData> {
  const applied: string[] = []
  const skippedFields: SkippedDraftField[] = []
  const next: ProductFormData = {
    ...current,
    pricing: { ...current.pricing },
    metadataEntries: [...current.metadataEntries],
    entitySelections: [...current.entitySelections]
  }

  const raw = fields as Record<string, unknown>
  for (const key of Object.keys(raw)) {
    const value = raw[key]
    if (value === undefined) continue

    switch (key) {
      case 'name':
        if (typeof value === 'string') {
          next.name = value
          applied.push(key)
        } else {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        }
        break
      case 'description':
        if (typeof value === 'string' || value === null) {
          next.description = value ?? ''
          applied.push(key)
        } else {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        }
        break
      case 'type':
        if (!isOneOf(value, PRODUCT_TYPES)) {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        } else if (value === current.type) {
          break
        } else if (options.isEditing) {
          skippedFields.push(skipped(key, 'immutableOnEdit'))
        } else {
          next.type = value
          applied.push(key)
        }
        break
      case 'visibility':
        if (!isOneOf(value, PRODUCT_VISIBILITIES)) {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        } else if (value === current.visibility) {
          break
        } else if (options.isEditing) {
          skippedFields.push(skipped(key, 'immutableOnEdit'))
        } else {
          next.visibility = value
          applied.push(key)
        }
        break
      case 'taxCode':
        if (typeof value === 'string' || value === null) {
          next.taxCode = value || undefined
          applied.push(key)
        } else {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        }
        break
      case 'metadata':
        if (isRecord(value)) {
          const entries = Object.entries(value)
            .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
            .map(([metadataKey, metadataValue]) => ({ key: metadataKey, value: metadataValue }))
          next.metadataEntries = entries.length > 0 ? entries : [{ key: '', value: '' }]
          applied.push(key)
        } else {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        }
        break
      case 'entitySelections':
        if (Array.isArray(value)) {
          next.entitySelections = toFormEntitySelections(value as ProductEntitySelectionResponse[])
          applied.push(key)
        } else {
          skippedFields.push(skipped(key, 'unsupportedValue'))
        }
        break
      case 'flatRatePricing':
      case 'perUnitPricing':
      case 'timeOfUsePricing':
      case 'volumePricing':
      case 'tariffPassThroughPricing':
        applyPricing(key, value, current, next, options, applied, skippedFields)
        break
      case 'organizationId':
      case 'replaceEntitySelections':
      case 'replacePricing':
        skippedFields.push(skipped(key, 'notInForm'))
        break
      default:
        skippedFields.push(skipped(key, 'unknownField'))
    }
  }

  return { next, applied, skipped: skippedFields }
}

function applyPricing(
  key: PricingKey,
  value: unknown,
  current: ProductFormData,
  next: ProductFormData,
  options: ProductDraftToFormOptions,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (!isRecord(value)) {
    skippedFields.push(skipped(key, 'unsupportedValue'))
    return
  }

  const target = PRICING_TARGET[key]
  const modelChanges =
    target.pricingModel !== current.pricing.pricingModel ||
    (target.pricingModel === 'USAGE_BASED' && target.usagePricingType !== current.pricing.usagePricingType)

  if (modelChanges && options.isEditing) {
    skippedFields.push(skipped(key, 'pricingModelImmutable'))
    return
  }

  if (modelChanges) {
    next.pricing.pricingModel = target.pricingModel
    next.pricing.usagePricingType = target.usagePricingType
    applied.push(`${key}.pricingModel`)
  }

  const currency = value.currency
  if (currency !== undefined) {
    const safe = getSafeCurrency(currency)
    if (safe !== currency) {
      skippedFields.push(skipped(`${key}.currency`, 'unsupportedValue'))
    } else if (safe !== current.pricing.currency) {
      if (options.isEditing) {
        skippedFields.push(skipped(`${key}.currency`, 'immutableOnEdit'))
      } else {
        next.pricing.currency = safe
        applied.push(`${key}.currency`)
      }
    }
  }

  const taxBehavior = value.taxBehavior
  if (taxBehavior !== undefined) {
    if (isOneOf(taxBehavior, TAX_BEHAVIORS)) {
      next.pricing.taxBehavior = taxBehavior
      applied.push(`${key}.taxBehavior`)
    } else {
      skippedFields.push(skipped(`${key}.taxBehavior`, 'unsupportedValue'))
    }
  }

  const billingType = value.billingType
  if (billingType !== undefined && (key === 'flatRatePricing' || key === 'volumePricing')) {
    if (isOneOf(billingType, BILLING_TYPES)) {
      next.pricing.billingType = billingType
      applied.push(`${key}.billingType`)
    } else {
      skippedFields.push(skipped(`${key}.billingType`, 'unsupportedValue'))
    }
  }

  const recurringInterval = value.recurringInterval
  if (recurringInterval !== undefined) {
    if (isOneOf(recurringInterval, BILLING_PERIODS)) {
      next.pricing.billingPeriod = recurringInterval
      applied.push(`${key}.recurringInterval`)
    } else {
      skippedFields.push(skipped(`${key}.recurringInterval`, 'unsupportedValue'))
    }
  }

  switch (key) {
    case 'flatRatePricing':
    case 'perUnitPricing':
      applyMinorAmount(`${key}.unitAmount`, value.unitAmount, next, applied, skippedFields)
      break
    case 'tariffPassThroughPricing':
      applyMinorAmount(`${key}.premium`, value.premium, next, applied, skippedFields)
      break
    case 'volumePricing':
      applyVolumePricing(value, next, applied, skippedFields)
      break
    case 'timeOfUsePricing':
      applyTimeOfUsePricing(value, next, options, applied, skippedFields)
      break
  }

  for (const subKey of Object.keys(value)) {
    if (!KNOWN_PRICING_SUBKEYS[key].has(subKey)) {
      skippedFields.push(skipped(`${key}.${subKey}`, 'unknownField'))
    }
  }
}

const COMMON_PRICING_SUBKEYS = ['currency', 'taxBehavior', 'billingType', 'recurringInterval']
const KNOWN_PRICING_SUBKEYS: Record<PricingKey, ReadonlySet<string>> = {
  flatRatePricing: new Set([...COMMON_PRICING_SUBKEYS, 'unitAmount']),
  perUnitPricing: new Set([...COMMON_PRICING_SUBKEYS, 'unitAmount', 'units']),
  tariffPassThroughPricing: new Set([...COMMON_PRICING_SUBKEYS, 'premium', 'premiumType', 'premiumPercent']),
  volumePricing: new Set([...COMMON_PRICING_SUBKEYS, 'units', 'tiersMode', 'tierQuantitySource', 'volumeTiers']),
  timeOfUsePricing: new Set([...COMMON_PRICING_SUBKEYS, 'rates', 'tariffRestrictions'])
}

function applyMinorAmount(
  path: string,
  value: unknown,
  next: ProductFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (value === undefined) return
  if (!isFiniteNumber(value)) {
    skippedFields.push(skipped(path, 'unsupportedValue'))
    return
  }
  next.pricing.amount = parseFromSmallestDenomination(value)
  applied.push(path)
}

function applyVolumePricing(
  value: Record<string, unknown>,
  next: ProductFormData,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (value.tiersMode !== undefined) {
    if (isOneOf(value.tiersMode, VOLUME_TIERS_MODES)) {
      next.pricing.tiersMode = value.tiersMode
      applied.push('volumePricing.tiersMode')
    } else {
      skippedFields.push(skipped('volumePricing.tiersMode', 'unsupportedValue'))
    }
  }
  if (value.tierQuantitySource !== undefined) {
    if (isOneOf(value.tierQuantitySource, TIER_QUANTITY_SOURCES)) {
      next.pricing.tierQuantitySource = value.tierQuantitySource
      applied.push('volumePricing.tierQuantitySource')
    } else {
      skippedFields.push(skipped('volumePricing.tierQuantitySource', 'unsupportedValue'))
    }
  }
  if (value.volumeTiers !== undefined) {
    if (!Array.isArray(value.volumeTiers)) {
      skippedFields.push(skipped('volumePricing.volumeTiers', 'unsupportedValue'))
      return
    }
    next.pricing.volumeTiers = value.volumeTiers.filter(isRecord).map((tier) => ({
      upTo: isFiniteNumber(tier.upTo) ? tier.upTo : null,
      unitAmount: parseFromSmallestDenomination(isFiniteNumber(tier.unitAmount) ? tier.unitAmount : 0),
      flatAmount: isFiniteNumber(tier.flatAmount) ? parseFromSmallestDenomination(tier.flatAmount) : null
    }))
    applied.push('volumePricing.volumeTiers')
  }
}

function applyTimeOfUsePricing(
  value: Record<string, unknown>,
  next: ProductFormData,
  options: ProductDraftToFormOptions,
  applied: string[],
  skippedFields: SkippedDraftField[]
): void {
  if (value.rates === undefined) return
  if (!Array.isArray(value.rates)) {
    skippedFields.push(skipped('timeOfUsePricing.rates', 'unsupportedValue'))
    return
  }
  const tzDayjs = options.tzDayjs ?? (() => dayjs())
  const currency = next.pricing.currency ?? getSafeCurrency(value.currency)
  const normalized = value.rates.filter(isRecord).map((rate) => normalizeDraftRate(rate, currency, tzDayjs))
  next.pricing.rates = parseTimeOfUseRatesToForm(normalized, tzDayjs)
  applied.push('timeOfUsePricing.rates')
}

/** Fills the fields the API marks required so a partial draft rate hydrates like a stored one. */
function normalizeDraftRate(
  rate: Record<string, unknown>,
  currency: string,
  tzDayjs: () => Dayjs
): BillableTimeOfUseRate {
  const periods = Array.isArray(rate.periods) ? rate.periods.filter(isRecord) : []
  return {
    ...(rate as Partial<BillableTimeOfUseRate>),
    name: typeof rate.name === 'string' ? rate.name : '',
    currency: typeof rate.currency === 'string' ? rate.currency : currency,
    validFrom: typeof rate.validFrom === 'string' ? rate.validFrom : tzDayjs().toISOString(),
    periods: periods.map((period) => ({
      name: typeof period.name === 'string' ? period.name : '',
      startTime: typeof period.startTime === 'string' ? period.startTime : '00:00:00',
      endTime: typeof period.endTime === 'string' ? period.endTime : '23:59:59',
      rate: isFiniteNumber(period.rate) ? period.rate : 0,
      daysOfWeek: isStringArray(period.daysOfWeek)
        ? (period.daysOfWeek as BillableTimeOfUseRate['periods'][number]['daysOfWeek'])
        : ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
    }))
  }
}

/**
 * Snapshot of the current form in the draft-fields (API request) shape, for the `billing_draft` context
 * ref. Reuses the wizard's own serializer when the form is complete enough; otherwise falls back to a
 * lenient field-by-field projection so an unfinished form still gives the assistant what the user sees.
 */
export function productFormToDraft(form: ProductFormData): Partial<CreateProductRequest> {
  try {
    const request: Partial<CreateProductRequest> = { ...parseProductFormData(form) }
    delete request.organizationId
    return request
  } catch {
    return lenientProductSnapshot(form)
  }
}

function lenientProductSnapshot(form: ProductFormData): Partial<CreateProductRequest> {
  const pricing = form.pricing
  const currency = getSafeCurrency(pricing.currency)
  const cents = (amount: number | undefined) => (isFiniteNumber(amount) ? Math.round(amount * 100) : undefined)
  const snapshot: Partial<CreateProductRequest> = {
    name: form.name,
    description: form.description || undefined,
    type: form.type,
    visibility: form.visibility,
    taxCode: form.taxCode || undefined,
    metadata: Object.fromEntries(
      form.metadataEntries.filter((entry) => entry.key.trim().length > 0).map((entry) => [entry.key, entry.value])
    ),
    entitySelections: form.entitySelections.map((selection) => ({
      entityType: selection.entityType,
      entityId: selection.entityId
    }))
  }

  const base = { currency, taxBehavior: pricing.taxBehavior, recurringInterval: pricing.billingPeriod ?? 'MONTH' }
  if (pricing.pricingModel === 'FLAT') {
    snapshot.flatRatePricing = {
      ...base,
      unitAmount: cents(pricing.amount) ?? 0,
      billingType: pricing.billingType ?? 'ONE_TIME'
    } as FlatRatePricing
  } else if (pricing.pricingModel === 'VOLUME_BASED') {
    snapshot.volumePricing = {
      ...base,
      billingType: pricing.billingType ?? 'ONE_TIME',
      units: pricing.tierQuantitySource === 'ENTITY_COUNT' ? 'PC' : 'KWH',
      tiersMode: pricing.tiersMode ?? 'GRADUATED',
      tierQuantitySource: pricing.tierQuantitySource ?? 'USAGE',
      volumeTiers: (pricing.volumeTiers ?? []).map((tier) => ({
        upTo: tier.upTo ?? undefined,
        unitAmount: cents(tier.unitAmount) ?? 0,
        flatAmount: tier.flatAmount != null ? cents(tier.flatAmount) : undefined
      }))
    } as VolumePricing
  } else if (pricing.usagePricingType === 'PER_UNIT') {
    snapshot.perUnitPricing = {
      ...base,
      unitAmount: cents(pricing.amount) ?? 0,
      units: 'KWH',
      billingType: 'ONE_TIME'
    } as PerUnitPricing
  } else if (pricing.usagePricingType === 'TARIFF_PASS_THROUGH') {
    snapshot.tariffPassThroughPricing = {
      ...base,
      premium: cents(pricing.amount) ?? 0,
      billingType: 'ONE_TIME'
    } as TariffPassThroughPricing
  } else {
    snapshot.timeOfUsePricing = {
      ...base,
      billingType: 'ONE_TIME',
      rates: (pricing.rates ?? []).map((rate) => ({
        name: rate.name,
        description: rate.description ?? undefined,
        currency,
        validFrom: rate.validFrom,
        validTo: rate.validTo ?? undefined,
        isYearlyRecurring: rate.isYearlyRecurring,
        isActive: rate.isActive,
        initiationFee: cents(rate.initiationFee),
        lockRateAtPlugIn: rate.lockRateAtPlugIn ?? false,
        idleFeeConfig: rate.hasIdleFee
          ? {
              rate: cents(rate.idleFee?.rate ?? undefined) ?? 0,
              gracePeriod: rate.idleFee?.gracePeriod ?? 0,
              stepSize: rate.idleFee?.stepSize ?? 0
            }
          : undefined,
        periods: (rate.periods ?? []).map((period) => ({
          name: period.name,
          startTime: period.startTime?.format('HH:mm:ss') ?? '00:00:00',
          endTime: period.endTime?.format('HH:mm:ss') ?? '23:59:59',
          rate: cents(period.rate) ?? 0,
          daysOfWeek: period.daysOfWeek
        }))
      }))
    } as TimeOfUsePricing
  }

  return snapshot
}
